import { useState, ReactNode } from 'react';

/**
 * CollapsibleSection - Reusable accordion component
 *
 * Features:
 * - Expand/collapse animation
 * - Chevron indicator
 * - Optional default open state
 * - Optional icon
 */

interface CollapsibleSectionProps {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
  icon?: string;
  // Optional toggle switch
  toggleEnabled?: boolean;
  onToggleChange?: (enabled: boolean) => void;
}

export function CollapsibleSection({
  title,
  children,
  defaultOpen = false,
  icon,
  toggleEnabled,
  onToggleChange,
}: CollapsibleSectionProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handleToggle = (): void => {
    setIsOpen(!isOpen);
  };

  const handleToggleSwitch = (e: React.MouseEvent): void => {
    e.stopPropagation(); // Prevent collapsing when clicking toggle
    if (onToggleChange) {
      onToggleChange(!toggleEnabled);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon && <span className="text-lg">{icon}</span>}
          <span className="font-medium text-gray-900">{title}</span>
        </div>

        <div className="flex items-center gap-3">
          {/* Toggle switch (if provided) */}
          {onToggleChange !== undefined && (
            <div
              onClick={handleToggleSwitch}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                toggleEnabled ? 'bg-sync-blue' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  toggleEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </div>
          )}

          {/* Chevron */}
          <span
            className={`text-gray-500 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
          >
            ▼
          </span>
        </div>
      </button>

      {/* Content */}
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? 'max-h-[2000px]' : 'max-h-0'
        }`}
      >
        <div className="p-4 space-y-4 bg-white">
          {children}
        </div>
      </div>
    </div>
  );
}
